// AnniversaryMoreMenu — kebab popover for the detail page (W3.1 §5.2).
//
// W31.1 removed the "跳到日曆 (stub)" entry. The Calendar module will ship
// as its own first-class surface in W6+, so dead-ending the user from a
// kebab menu into a fake route no longer makes sense. The popover now only
// hosts [編輯] [複製連結] [刪除].
//
// Note: @radix-ui/react-popover@1.1.x doesn't expose a Popover.Item primitive
// (the spec covers it but the types don't). We use plain buttons inside
// Popover.Content — the dismissable layer handles outside-click auto-close.

import { useRef } from "react";
import * as Popover from "@radix-ui/react-popover";
import { MoreHorizontal, Edit3, Copy, Trash2 } from "lucide-react";

import { HoldToConfirmButton } from "@/components/system/HoldToConfirmButton";

import { cn } from "@/lib/utils/cn";
import { useToast } from "@/components/ui/toast";
import {
  POPOVER_GAP,
  POPOVER_PANEL_CLASS,
  POPOVER_ROW_CLASS,
  POPOVER_TILE_CLASS,
  usePopoverPlacement,
} from "@/components/system/Popover";

export interface AnniversaryMoreMenuProps {
  anniversaryId: string;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

export function AnniversaryMoreMenu({
  anniversaryId,
  canEdit,
  onEdit,
  onDelete,
}: AnniversaryMoreMenuProps) {
  const copyHref = `${window.location.origin}/anniversaries/${anniversaryId}`;
  // W52.3. Radix measures and positions the panel itself, including collision
  // avoidance — that is worth keeping rather than reimplementing. What Radix
  // cannot know is *which edge we want*, and `align="end"` was that decision
  // hardcoded at the one place the kebab happened to sit. The shared rule reads
  // the trigger and hands Radix the answer.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const placement = usePopoverPlacement(triggerRef, "bottom");
  // W40.5. The copy row was the one control the deep sweep could not clear, and
  // it was not dead: wrapping `navigator.clipboard` showed `writeText` called
  // and resolved, with **no visible change on the page**. So the button worked
  // and said nothing — and because the failure branch was a bare `catch {}`,
  // success, 「the browser has no clipboard」 and 「the browser refused」 all
  // looked identical to a person. Pressing it and seeing nothing is how a user
  // learns to press it three times.
  //
  // ADR-0079/0080 are about copy that *states* something untrue; this is the
  // other half — an action whose outcome is unobservable. The toast is the
  // codebase's existing primitive (`aria-live="polite"`, FIFO, 2.5 s) rather
  // than a second mechanism invented here.
  const { push: pushToast } = useToast();

  const handleCopy = async () => {
    if (!navigator.clipboard?.writeText) {
      pushToast({ message: "這個瀏覽器不支援複製，連結已選取請手動複製", variant: "danger" });
      return;
    }
    try {
      await navigator.clipboard.writeText(copyHref);
      pushToast({ message: "已複製連結", variant: "success" });
    } catch {
      pushToast({ message: "複製失敗，請手動複製網址", variant: "danger" });
    }
  };

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label="更多動作"
          data-testid="anniversary-more"
          className="tap-target inline-flex h-11 w-11 items-center justify-center rounded-full text-ink-2 hover:bg-black/5 press"
        >
          <MoreHorizontal size={20} aria-hidden />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          // From the shared rule, not a literal.
          align={placement.radixAlign}
          sideOffset={POPOVER_GAP}
          // **No `side` prop.** Radix already does collision detection, and it
          // does it better than a hook that only knows the trigger's box —
          // forcing a side on it produced a panel that *overlapped* its own
          // trigger (measured −121px) when Radix's own flip disagreed with ours.
          // It gets `align` (which edge we want) and `sideOffset` (how far),
          // and keeps `side` to itself.
          data-align={placement.align}
          // W52.3 — the only menu in the app that was not a menu to assistive
          // tech: Radix's `Content` had no `role="menu"` and its rows no
          // `role="menuitem"`, so a screen reader announced these three as
          // loose buttons. The audit found it by counting rows the same way it
          // counts them on the other four.
          role="menu"
          aria-label="更多動作"
          className={cn("z-50 w-44", POPOVER_PANEL_CLASS)}
        >
          {canEdit ? (
            <button
              type="button"
              role="menuitem"
              onClick={onEdit}
              data-testid="anniversary-menu-edit"
              className={POPOVER_ROW_CLASS}
            >
              <span className={POPOVER_TILE_CLASS}>
                <Edit3 size={17} aria-hidden />
              </span>
              編輯
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={() => void handleCopy()}
            data-testid="anniversary-menu-copy"
            className={POPOVER_ROW_CLASS}
          >
            <span className={POPOVER_TILE_CLASS}>
              <Copy size={17} aria-hidden />
            </span>
            複製連結
          </button>
          {canEdit ? (
            /* W50.1 §1 — hold-to-confirm, so opening the menu and tapping the
               destructive row by accident is no longer enough to delete.
               It stays a `HoldToConfirmButton` rather than a plain row: the
               press-and-hold gesture *is* the confirmation, and replacing it
               with the shared row would have put a one-tap 刪除紀念日 back. */
            <HoldToConfirmButton
              onConfirm={onDelete}
              label="刪除紀念日"
              icon={
                <span className={POPOVER_TILE_CLASS}>
                  <Trash2 size={17} aria-hidden />
                </span>
              }
              className={cn(POPOVER_ROW_CLASS, "text-partner-strong hover:bg-white/10")}
              role="menuitem"
              testId="anniversary-menu-delete"
            >
              刪除
            </HoldToConfirmButton>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}